import { reportBelongsToSite } from './reportSiteIdentity';
/**
 * Site-level scoring helper for type-aware dashboards.
 *
 * Sites can carry multiple report types (feasibility, environmental, etc.).
 * The dashboard's "Top Ranked Sites" + the Compare grid need a single score
 * per site, optionally filtered to one report type.
 *
 * Rules:
 *   - When `filterType` is provided, returns the latest successfully saved,
 *     calculation-usable matching report's overallScore + status.
 *   - When `filterType` is undefined, returns the latest usable saved report
 *     across all types on the site.
 *   - Reports are matched on `report.siteId === site.id` first, then on
 *     case-insensitive site-name match (legacy reports without siteId).
 *
 * Pure helper — no store, no I/O — so the dashboard can call it inline
 * inside `Array.prototype.sort`.
 */
import type { SiteEntity } from '../types/site';
import type { SiteRagReport, RagStatus, ReportOverallStatus } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import { reportCanContributeSavedResult } from './pathToGo/reportPlan';

export interface SiteSummary {
  /** 0..100, mirrors report.overallScore. */
  score: number;
  /** RAG status for the dashboard pill colour. */
  status: ReportOverallStatus;
  /** The report the score was sourced from. */
  report: SiteRagReport;
}

function reportsForSite(
  site: Pick<SiteEntity, 'id' | 'name'>,
  allReports: ReadonlyArray<SiteRagReport>,
): SiteRagReport[] {
  return allReports.filter(report => reportBelongsToSite(site, report));
}

export function siteSummaryScore(
  site: Pick<SiteEntity, 'id' | 'name'>,
  allReports: ReadonlyArray<SiteRagReport>,
  filterType?: ReportTypeId,
): SiteSummary | null {
  const matched = reportsForSite(site, allReports);
  const filtered = (filterType
    ? matched.filter(r => (r.reportType ?? 'feasibility') === filterType)
    : matched).filter(report => reportCanContributeSavedResult(
      report,
      (report.reportType ?? 'feasibility') as ReportTypeId,
    ));
  if (filtered.length === 0) return null;

  // Latest usable saved result wins: max version, then createdAt tie-breaker.
  let best = filtered[0]!;
  for (const r of filtered) {
    const bestV = best.version ?? 1;
    const candV = r.version ?? 1;
    if (candV > bestV) { best = r; continue; }
    if (candV < bestV) continue;
    if (new Date(r.createdAt).getTime() > new Date(best.createdAt).getTime()) best = r;
  }
  return { score: best.overallScore, status: best.overallStatus, report: best };
}

/** True when the site has at least one report of the given type. */
export function siteHasReportType(
  site: Pick<SiteEntity, 'id' | 'name'>,
  allReports: ReadonlyArray<SiteRagReport>,
  filterType: ReportTypeId,
): boolean {
  return reportsForSite(site, allReports).some(
    r => (r.reportType ?? 'feasibility') === filterType,
  );
}
